import {
  CommonTag,
  Filter,
  FilterComparison,
  FilterLogic,
  LinkedDataConnectorStatus,
  TransactionRuleCriteriaType,
  TransactionRuleType
} from '@types'

export type TransactionRuleCriteria = Filter

export type TransactionRuleCriteriaLegacy = {
  logic: FilterLogic
  type: TransactionRuleCriteriaType
  params: {
    comparison: FilterComparison | undefined
    value: any
  }[]
}

export enum RuleApplyStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  PARTIAL = 'partial',
  NOT_APPLIED = 'not_applied',
}

export enum MappingStatus {
  OK = 'ok',
  FAILED = 'failed',
  PENDING = 'pending',
}

export type TransactionRule<T> = {
  type: TransactionRuleType
  is_active?: boolean
  order?: number
  data: {
    criteria: TransactionRuleCriteria
    actions: T
  }
  id?: string
  created_at?: string
  updated_at?: string
  last_applied_at?: string | null
  apply_status?: RuleApplyStatus | null
  apply_to_existing_transactions?: boolean
}

export type ApplyRuleToTransactionsResult = {
  transactions: LinkedDataTransaction[]
  apply_status: RuleApplyStatus
}

export type ConnectorReferencingRule = {
  id: string
  description: string | null
  repository_id: string | null
}

type LinkedDataConnectorRepository = {
  id: string
  name: string
}

export type LinkedDataConnector = {
  id: string
  logo: string | null
  name: string
  status: LinkedDataConnectorStatus
  status_message: string
  access_method: string
  repositories: LinkedDataConnectorRepository[]
  source: string
  is_user_connectable: boolean
  user_id?: string
  created_by?: string
  creator_email?: string
}

export type LinkedDataAccount = {
  id: string
  account_name: string
  account_number?: string
  account_type: string
  balance_amount: number
  balance_currency: string
  total_transactions_amount: number
  connector_id: string
  connector_name: string
  repo_map: {
    allposit_id: string
    linked: boolean
  }
  sync_mode: 'balance'
  account_last_updated: string
  last_update_status: 'SUCCESS'
  created_at: string
  updated_at: string
  deleted: boolean
}

export type LinkedDataTransaction = {
  id: string
  account_id: string
  account_name: string
  account_number: string
  account_type: string
  allposit_transaction_id: string | null
  amount: number
  category: string
  subcategory: string
  description: string
  // Null when no manual correction is set; `description` always carries the provider text.
  override_description: string | null
  connector_id?: string
  connector_name: string
  access_method: string
  status: string
  subtype: string | null
  tags: CommonTag[]
  date: string
  override_date: string | null
  type: string | null
  source: string
  mapping_status?: MappingStatus | null
  mapping_message?: string | null
}

// Mirrors `LinkedDataTransactionUpdate` in allposit/resources/LinkedData/models.py. Written out
// rather than derived from the response type: the API sets `extra="forbid"` and rejects anything
// else with a 400, including the provider-owned `date` and `description`. Deriving it from the
// response would let a type-checked caller build a payload the server refuses.
export type LinkedDataTransactionUpdate = Pick<LinkedDataTransaction, 'id'> &
  Partial<
    Pick<
      LinkedDataTransaction,
      | 'override_date'
      | 'override_description'
      | 'type'
      | 'status'
      | 'category'
      | 'subcategory'
      | 'amount'
    > & {
      // Not on the response type above, though the API returns it - a pre-existing gap.
      counterparty: string
      tags: string[]
    }
  >

export type LinkedDataPosition = {
  id: string
  allposit_id?: string
  account_id: string
  account_name: string
  as_of_date: string
  connector_id: string
  connector_name: string
  description: string
  map_id: string | null
  mapped_asset_id: string | null
  name: string
  price: number
  quantity: number
  security_id: string
  security_id_type: string | null
  ticker: string
  value: number
}

type LinkedDataConnectorPositionData = {
  symbol: string
  description: string
  qty: number
  value: number
  price: number
  allposit_id?: string
}

type LinkedDataConnectorPositionRawJson<T extends { id: string }> = {
  [P in keyof T]: T[P]
}

export type LinkedDataConnectorItemPosition = {
  raw_json: LinkedDataConnectorPositionRawJson<any>
  normalized_data: LinkedDataConnectorPositionData
}

type LinkedDataConnectorDataset = {
  additional_status: string
  last_update_attempt: string
  last_updated: string
  name: string
  next_update_scheduled: string
  update_eligibility: string
}

export type LinkedDataConnectorItemData = {
  account_name: string
  account_number: string
  account_status: string
  account_type: string
  aggregation_source: string
  balance: { amount: number; currency: string }
  classification: string
  container: string
  created_date: string
  dataset: LinkedDataConnectorDataset[]
  displayed_name: string
  due_date: string
  frequency: string
  id: number
  include_in_net_worth: boolean
  interest_rate_type: string
  is_asset: boolean
  is_manual: boolean
  last_payment_date: string
  last_updated: string
  maturity_date: string
  original_loan_amount: { amount: number; currency: string }
  origination_date: string
  principal_balance: { amount: number; currency: string }
  provider_account_id: number
  connector_id: string
  connector_name: string
}

type LinkedDataConnectorEventStatus = 'pending' | 'success' | 'error'

export type LinkedDataConnectorItemEventType =
  | 'account_positions_sync'
  | 'accounts_sync'

export type LinkedDataConnectorItemEvent = {
  id: string
  created_at: string
  status: LinkedDataConnectorEventStatus
  status_message: string
  event_type: LinkedDataConnectorItemEventType
  position_count?: number
}

export type LinkedDataConnectorItemEvents = {
  account_id: string
  last_sync: string
  last_sync_status: string
  first_sync: string
  events: LinkedDataConnectorItemEvent[]
  has_more: boolean
}

export type LinkedDataSyncEventsRange = {
  date_from?: string
  date_to?: string
}

export type FastLinkSuccessResponse = {
  fnToCall: string
  providerName: string
  requestId: string
  providerAccountId: number
  status: string
  providerId: number
  additionalStatus: string
}

export type FastLinkErrorResponse = {
  code?: string
  fnToCall?: string
  message: string
  redirectUrl?: string
  title?: string
}

export type FastLinkCloseResponse = {
  action: string
  fnToCall: string
  sites: unknown[]
  status?: string
}

export type PlaidOnSuccessResponseMetadata = {
  accounts: {
    id: string
    mask: string
    name: string
    type: string
    subtype: string
    verification_status: string
    class_type: string
  }
  institution: {
    institution_id: string
    name: string
  }
  link_session_id: string
  request_id: string
}

export type PlaidOnEventResponseMetadata = {
  account_number_mask: string | null
  error_code: string | null
  error_message: string | null
  error_type: string | null
  exit_status: string | null
  institution_id: string | null
  institution_name: string | null
  institution_search_query: string | null
  is_update_mode: string | null
  match_reason: string | null
  routing_number: string | null
  mfa_type: string | null
  view_name: string | null
  request_id: string
  link_session_id: string
  timestamp: string
  selection: string | null
}

export type PlaidOnExitResponseError = {
  display_message: string
  error_code: string
  error_message: string
  error_type: string
}

export type PlaidOnExitResponseMetadata = {
  institution: {
    institution_id: string
    name: string
  }
  link_session_id: string
  request_id: string
  status: string
}

export type LinkedDataAccountMappingData = {
  linked_id: string
  repository_id?: string
  asset_id: string
  action: 'add' | 'delete'
}

export type LinkedDataPositionMappingData = {
  map_id: string
  repository_id?: string
  asset_id: string
}

export type PivotFetchData = {
  rows: string[]
  cols: string[]
  values: string[]
  filter?: Filter
}

export type PlaidToken = {
  expiration: string
  link_token: string
  request_id: string
}
